import gql from "graphql-tag";
import { reportActionError } from "~/core/store/common/errors/handleStoreError.ts";
import {
  composeGiftOrderComment,
  getGiftOrderPreference,
} from "~/core/utils/giftOrder";

export const CONFIRM_ORDER_MUTATION = gql`
  mutation confirmOrder($withPayment: Boolean = true, $comment: String) {
    confirmOrder(withPayment: $withPayment, comment: $comment) {
      order {
        id
        clientSecret
        paymentIntentId
      }
      url
      callback
    }
  }
`;
export const state = () => ({
  order: null,
  url: "",
  paymentError: null,
  details: null,
  updateOrderResponse: null,
  confirmOrderDetails: null,
  isConfirmOrderSuccessfully: false,
  clientSecret: null,
  paymentIntentId: null,
  refreshToken: null,
});

export const getters = {
  order(state) {
    return state.order;
  },
  isConfirmOrderSuccessfully(state) {
    return state.isConfirmOrderSuccessfully;
  },
  url(state) {
    return state.url;
  },
  paymentError(state) {
    return state.paymentError;
  },
  details(state) {
    return state.details;
  },
  updateOrderResponse(state) {
    return state.updateOrderResponse;
  },
  confirmOrderDetails(state) {
    return state.confirmOrderDetails;
  },
  clientSecret(state) {
    return state.clientSecret;
  },
  paymentIntentId(state) {
    return state.paymentIntentId;
  },
  orderId(state) {
    return state.orderId;
  },
};

export const mutations = {
  setOrder(state, order) {
    state.order = order;
  },
  setIsConfirmOrderSuccessfully(state, payload) {
    state.isConfirmOrderSuccessfully = payload;
  },
  setUrl(state, payload) {
    state.url = payload;
  },
  setPaymentError(state, payload) {
    state.paymentError = payload;
  },
  clearPaymentError(state) {
    state.paymentError = null;
  },
  setOrderDetails(state, payload) {
    // Merge (not replace) so a partial commit — e.g. the billing-only
    // { paymentAddress, paymentAddressId } from new-payment-section — can never
    // silently drop the already-set shippingAddress / shippingAddressId /
    // shippingMethod / paymentMethod set by earlier steps.
    state.details = { ...state.details, ...payload };
  },
  setConfirmOrderDetails(state, payload) {
    state.confirmOrderDetails = payload;
  },
  setUpdateOrderResponse(state, payload) {
    state.updateOrderResponse = payload;
  },
  setClientSecret(state, secret) {
    state.clientSecret = secret;
  },
  setPaymentIntentId(state, id) {
    state.paymentIntentId = id;
  },
  setOrderId(state, orderId) {
    state.orderId = orderId;
  },
  setRefreshToken(state, token) {
    state.refreshToken = token;
  },
  setPaymentIntentStatus(state, status) {
    state.paymentIntentStatus = status;
  },
  setStripePublicKey(state, stripePublicKey) {
    state.stripePublicKey = stripePublicKey;
  },
};

export const actions = {
  async express(_context, input) {
    const { data } = await this.$vfapollo.mutate({
      mutation: gql`
        mutation expressCheckout($input: String!) {
          expressCheckout(input: $input)
        }
      `,
      variables: { input: JSON.stringify(input) },
    });
    return JSON.parse(data.expressCheckout);
  },
  async create({ commit }) {
    try {
      await this.$vfapollo.mutate({
        mutation: gql`
          mutation {
            createOrder {
              success
            }
          }
        `,
      });
    } catch (e) {
      reportActionError({ commit }, e, { showNotification: true });
    }
  },

  async update({ commit, state, dispatch }, payload) {
    commit("setUpdateOrderResponse", null);
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: gql`
          mutation (
            $paymentAddress: [InputField]
            $paymentAddressId: String
            $shippingAddressId: String
            $shippingAddress: [InputField]
            $paymentMethod: String
            $shippingMethod: String
          ) {
            updateOrder(
              paymentAddress: $paymentAddress
              shippingAddress: $shippingAddress
              shippingAddressId: $shippingAddressId
              paymentAddressId: $paymentAddressId
              paymentMethod: $paymentMethod
              shippingMethod: $shippingMethod
            ) {
              shippingMethods {
                id
                codename
                name
              }
              totals {
                title
                text
              }
            }
          }
        `,
        variables: state.details,
      });
      commit("setUpdateOrderResponse", data.updateOrder);
      if (data.updateOrder) {
        // A validated, saved checkout identity enables the consented guest
        // funnel. This is identification only, never a marketing subscription.
        void dispatch(
          "common/klaviyo/identifyCheckout",
          state.details?.shippingAddress || state.details?.paymentAddress,
          { root: true }
        ).catch(() => {});
      }
      return data;
    } catch (e) {
      reportActionError({ commit }, e, { showNotification: true });
    }
  },

  async init({ commit }) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: gql`
          mutation initPayment {
            initPayment {
              refresh
              paymentIntentId
              clientSecret
              stripePublicKey
              paymentIntentStatus
            }
          }
        `,
      });

      const paymentData = data?.initPayment;
      commit("setClientSecret", paymentData.clientSecret);
      commit("setPaymentIntentId", paymentData.paymentIntentId);
      commit("setPaymentIntentStatus", paymentData.paymentIntentStatus);
      commit("setStripePublicKey", paymentData.stripePublicKey);

      if (paymentData.refresh) {
        commit("setRefreshToken", paymentData.refresh);
      }
      return paymentData;
    } catch (e) {
      reportActionError({ commit }, e, { showNotification: true });
      return null;
    }
  },

  async confirm({ commit, state }, { elements, withPayment, onSuccess }) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: CONFIRM_ORDER_MUTATION,
        variables: {
          withPayment,
          comment: composeGiftOrderComment(
            state.details?.comment,
            getGiftOrderPreference()
          ),
        },
      });

      const { order, url, callback } = data.confirmOrder;

      commit("setOrder", order);
      commit("setOrderId", order.id);
      commit("setUrl", url);
      // commit("setClientSecret", order.clientSecret);
      commit("setPaymentIntentId", order.paymentIntentId);
      if (typeof onSuccess === "function") {
        onSuccess({
          order,
          clientSecret: order.clientSecret,
          paymentIntentId: order.paymentIntentId,
          callback,
          url,
        });
      }

      return {
        status: "succeed",
        order,
        clientSecret: order.clientSecret,
        paymentIntentId: order.paymentIntentId,
        callback,
        url,
      };
    } catch (e) {
      console.error("GraphQL confirmOrder error:", e?.message || e);
      // No toast here: the checkout UI shows its own error modal from the
      // returned { error, message } object.
      reportActionError({ commit }, e);
      return { error: true, message: e?.message || "Unknown GraphQL error" };
    }
  },

  async confirmKlarna(
    { commit, state },
    { withPayment, paymentMethod, onSuccess }
  ) {
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: CONFIRM_ORDER_MUTATION,
        variables: {
          withPayment,
          paymentMethod,
          comment: composeGiftOrderComment(
            state.details?.comment,
            getGiftOrderPreference()
          ),
        },
      });

      const { order, url, clientSecret, paymentIntentId } = data.confirmOrder;

      commit("setOrder", order);
      commit("setUrl", url);
      commit("setClientSecret", clientSecret);
      commit("setPaymentIntentId", paymentIntentId);

      if (typeof onSuccess === "function") {
        onSuccess({
          order,
          clientSecret,
          paymentIntentId,
        });
      }

      return {
        order,
        clientSecret,
        paymentIntentId,
      };
    } catch (e) {
      console.error("GraphQL Klarna error:", e?.message || e);
      // No toast here: the checkout UI shows its own error modal from the
      // returned { error, message } object.
      reportActionError({ commit }, e);
      return { error: true, message: e?.message || "Unknown GraphQL error" };
    }
  },
};
export const namespaced = true;
//# sourceMappingURL=order.mjs.map
